import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Chip, StatusGlyph } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { RefreshIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { activateOnKey } from "utils/activateOnKey";
import { type ContactSubscription } from "utils/contacts/recurringBilling";
import {
  CADENCE_LABEL_KEY,
  CADENCE_SUFFIX_KEY,
  money,
  shortDate,
} from "utils/contacts/billingFormat";

interface RecurringBillingPanelProps {
  subscriptions: ContactSubscription[];
  monthlyValueCents: number;
  /**
   * False until there is a subscriptions backend. An empty list then means
   * "not wired up yet" rather than "this customer has no plans", and the two
   * are different claims to make on a customer's record.
   */
  connected: boolean;
  onSelectSubscription: (subscription: ContactSubscription) => void;
}

const RecurringBillingPanel: React.FC<RecurringBillingPanelProps> = ({
  subscriptions,
  monthlyValueCents,
  connected,
  onSelectSubscription,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Card variant="outlined" sx={{ padding: "var(--space-6)" }}>
      <Stack gap="var(--space-5)">
        <Typography variant="h5">{t("Recurring billing")}</Typography>

        {subscriptions.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              paddingBlock: "var(--space-6)",
              border: "1px dashed var(--color-border-subtle)",
              borderRadius: "var(--radius-md)",
            }}
          >
            {connected
              ? t("No subscriptions for this customer")
              : t("RecurringBillingNotConnected")}
          </Typography>
        ) : (
          <Stack gap="var(--space-5)">
            <Inline
              justify="between"
              align="center"
              sx={{
                backgroundColor: "var(--color-bg-elevated)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-4)",
              }}
            >
              <Inline gap="var(--space-3)" align="center" wrap={false}>
                <HugeiconsIcon
                  icon={RefreshIcon}
                  size={14}
                  color="var(--color-text-brand)"
                />
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  {t("Monthly value")}
                </Typography>
              </Inline>
              {/* Rounded to whole currency: the rolled-up figure is an
                  estimate once a weekly plan is in it, and cents imply a
                  precision the arithmetic does not have. */}
              <Typography variant="h5" component="span">
                {money(monthlyValueCents, 0, i18n.language)}
                <Typography component="span" variant="caption">
                  {t("PerMonthShort")}
                </Typography>
              </Typography>
            </Inline>

            <Stack gap="var(--space-5)">
              {subscriptions.map((subscription) => {
                const canceled = subscription.status === "CANCELED";
                return (
                  //  TODO: Replace this with ListItemButton component once it's available
                  <Inline
                    key={subscription.id}
                    justify="between"
                    align="start"
                    wrap={false}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectSubscription(subscription)}
                    onKeyDown={activateOnKey(() =>
                      onSelectSubscription(subscription),
                    )}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "var(--radius-md)",
                      padding: "var(--space-3)",
                      "&:hover": {
                        backgroundColor: "var(--color-bg-surface-hover)",
                      },
                    }}
                  >
                    <Stack gap="var(--space-1)">
                      <Typography variant="subtitle2">
                        {subscription.planName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "var(--color-text-tertiary)" }}
                      >
                        {money(subscription.amountCents, 2, i18n.language)}
                        {t(CADENCE_SUFFIX_KEY[subscription.cadence])}
                        {" · "}
                        {t(CADENCE_LABEL_KEY[subscription.cadence])}
                      </Typography>
                    </Stack>

                    <Stack gap="var(--space-1)" sx={{ alignItems: "flex-end" }}>
                      <Chip
                        size="sm"
                        tone={canceled ? "negative" : "positive"}
                        leading={
                          <StatusGlyph
                            status={canceled ? "Canceled" : "Active"}
                            tone={canceled ? "negative" : "positive"}
                          />
                        }
                      >
                        {canceled ? t("Canceled") : t("Active")}
                      </Chip>
                      <Typography
                        variant="caption"
                        sx={{ color: "var(--color-text-tertiary)" }}
                      >
                        {subscription.nextChargeAt
                          ? t("NextChargeOn", {
                              date: shortDate(
                                subscription.nextChargeAt,
                                i18n.language,
                              ),
                            })
                          : t("Not scheduled")}
                      </Typography>
                    </Stack>
                  </Inline>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default RecurringBillingPanel;
